import { put, takeEvery, call, type SagaReturnType } from 'redux-saga/effects'
import { showNotification } from 'src/components/parts/notifications/notifications'
import {
    getUserParkingHistory,
    getUserStats,
    getSafetyControlSystemData,
    persistUserReview,
    rectifyParkingDecision,
} from 'src/api/fm/safetyControlSystem/safetyControlSystem'
import {
    setSafetyControlUserStats,
    setSafetyControlUserParkingHistory,
    setSafetyControlSystemInfo,
    getSafetyControlInfo,
    markNoMoreUsers,
    clearSafetyControlInfo,
} from 'src/redux/safetyControlSystem/safetyControlSystem.actions'
import {
    SAFETY_CONTROL_SYSTEM_INFO,
    SAFETY_CONTROL_SYSTEM_DATA,
    SEND_ACTION_REVIEW,
    GET_USER_PARKING_HISTORY,
    RECTIFY_PARKING_DECISION,
    type GetSafetyControlUserStats,
    type SendUserReviewAction,
    type RectifyParkingDecision,
} from 'src/redux/safetyControlSystem/safetyControlSystem.types'
import { getRiderRideHistory, resetRiderRideHistory } from 'src/redux/rider'
import { getVehicleRideHistory, resetScooterRideHistory } from 'src/redux/scooter'

type GetSafetyControlSystemData = SagaReturnType<typeof getSafetyControlSystemData>

export function* fetchSafetyControlSystemInfo() {
    const safetyControlSystemData: GetSafetyControlSystemData = yield call(getSafetyControlSystemData)

    if (safetyControlSystemData instanceof Error) {
        yield call(showNotification, 'error', safetyControlSystemData.message)
    } else {
        if (safetyControlSystemData) {
            yield put(setSafetyControlSystemInfo(safetyControlSystemData))
        } else {
            yield put(clearSafetyControlInfo())
            yield put(markNoMoreUsers())
        }
    }
}

type GetUserStats = SagaReturnType<typeof getUserStats>
type GetUserParkingHistory = SagaReturnType<typeof getUserParkingHistory>

export function* fetchSafetyControlSystemData(action: GetSafetyControlUserStats) {
    const { userId, zoneId, eventType } = action.payload

    const userStats: GetUserStats = yield call(getUserStats, userId, zoneId)

    if (userStats instanceof Error) {
        yield call(showNotification, 'error', userStats.message)
    } else {
        yield put(setSafetyControlUserStats(userStats))
    }

    const userParkingHistory: GetUserParkingHistory = yield call(getUserParkingHistory, userId, zoneId, eventType)

    if (userParkingHistory instanceof Error) {
        yield call(showNotification, 'error', userParkingHistory.message)
    } else {
        yield put(setSafetyControlUserParkingHistory(userParkingHistory))
    }
}

type PersistUserReview = SagaReturnType<typeof persistUserReview>

export function* sendUserReviewAction(action: SendUserReviewAction) {
    const { backlogId, actionReview } = action.payload
    const result: PersistUserReview = yield call(persistUserReview, backlogId, actionReview)

    if (result instanceof Error) {
        if (!result.message.startsWith('409 ErrReviewCompleteConflict')) {
            yield call(showNotification, 'error', result.message)
            return
        }
    }

    yield put(getSafetyControlInfo())
}

function* fetchUserParkingHistory(action: GetSafetyControlUserStats) {
    const { userId, zoneId, eventType } = action.payload

    const userParkingHistory: GetUserParkingHistory = yield call(getUserParkingHistory, userId, zoneId, eventType)

    if (userParkingHistory instanceof Error) {
        yield call(showNotification, 'error', userParkingHistory.message)
    } else {
        yield put(setSafetyControlUserParkingHistory(userParkingHistory))
    }
}

type RectifyParkingDecisionResult = SagaReturnType<typeof rectifyParkingDecision>

export function* rectifyParkingDecisionAction(action: RectifyParkingDecision) {
    const INITIAL_PAGE_CURSOR = ''
    const { rideId } = action.payload
    const result: RectifyParkingDecisionResult = yield call(rectifyParkingDecision, rideId)

    if (result instanceof Error) {
        yield call(showNotification, 'error', result.message)
        return
    }

    showNotification('success', 'Parking decision successfully rectified')

    yield put(resetRiderRideHistory())
    yield put(getRiderRideHistory(INITIAL_PAGE_CURSOR))

    // Please note that rectification can be done also on the scooter ride history level
    yield put(resetScooterRideHistory())
    yield put(getVehicleRideHistory(INITIAL_PAGE_CURSOR))
}

export default function* watcher() {
    yield takeEvery(SAFETY_CONTROL_SYSTEM_INFO, fetchSafetyControlSystemInfo)
    yield takeEvery(SAFETY_CONTROL_SYSTEM_DATA, fetchSafetyControlSystemData)
    yield takeEvery(SEND_ACTION_REVIEW, sendUserReviewAction)
    yield takeEvery(GET_USER_PARKING_HISTORY, fetchUserParkingHistory)
    yield takeEvery(RECTIFY_PARKING_DECISION, rectifyParkingDecisionAction)
}
